import { FunctionComponent, useEffect, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { useTranslation } from 'react-i18next'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import { Daycare } from '@/types/Daycare'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import { useMutation } from '@apollo/client'
import { CANCEL_SLOT_REQUEST } from '@/modules/API/slot/requestSlotsHandler'
import styles from './cancelRequestSlot.module.scss'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Check, Close } from '@mui/icons-material'
import { ChildRequestSlot } from '@/types/Child'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface CancelSlotRequestProps {
    handleClose: () => void
    currentDaycare: Daycare
    refetchData: () => void
    closeParentDialog: () => void
    childRequestSlots: ChildRequestSlot[]
}

const CancelSlotRequest: FunctionComponent<CancelSlotRequestProps> = ({
    handleClose,
    currentDaycare,
    refetchData,
    closeParentDialog,
    childRequestSlots
}: CancelSlotRequestProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [showDialog, setShowDialog] = useState<boolean>(false)

    const [selectChilds, setSelectChilds] = useState<string[]>(
        childRequestSlots.map((child) => {
            return child.slotId.toString()
        })
    )

    const selectOptionsChild: SelectOptionsDeprecated = [
        {
            options: childRequestSlots.map((child: ChildRequestSlot): SelectOptionDeprecated => {
                return {
                    name: child.childName + (child.childComplement ? ' - ' + child.childComplement : ''),
                    value: child.slotId.toString()
                }
            })
        }
    ]

    function cancelAllRequestSlot(): void {
        childRequestSlots
            .filter((slot: ChildRequestSlot): boolean => selectChilds.includes(slot.slotId.toString()))
            .map((slot: ChildRequestSlot): void => {
                cancelRequestSlot({
                    variables: {
                        daycare_id: currentDaycare.id,
                        id: slot.slotId,
                        child_id: slot.childId
                    }
                })
            })
    }

    const [cancelRequestSlot, { loading: loadingCancel }] = useMutation(CANCEL_SLOT_REQUEST, {
        onCompleted: () => {
            refetchData()
            closeParentDialog()

            showNotificationSuccess(t('slotRequestCancelled'))
        },
        onError: () => {
            showNotificationError(t('errorCancelledSlotRequest'))
        }
    })

    useEffect(() => {
        if (childRequestSlots.length === 1) {
            cancelRequestSlot({
                variables: {
                    daycare_id: currentDaycare.id,
                    id: childRequestSlots[0].slotId,
                    child_id: childRequestSlots[0].childId
                }
            })
            setShowDialog(false)
        } else {
            setShowDialog(true)
        }
    }, [])

    return (
        <>
            {showDialog && (
                <Dialog open={showDialog} handleClose={handleClose}>
                    <DialogHeader title={t('cancelSlotRequest')} parent />
                    <DialogBody className={styles.DialogBody}>
                        <p className={styles.Text}>{t('cancelRequestSlotMessage')}</p>
                        {childRequestSlots.length > 1 && (
                            <SelectMultiple
                                selectOptions={selectOptionsChild}
                                selectValues={selectChilds}
                                setSelectValues={setSelectChilds}
                                className={styles.UserInput}
                                isParent
                            />
                        )}
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon
                                type={'custom'}
                                customText={t('yes')}
                                customIcon={<Check />}
                                onClick={cancelAllRequestSlot}
                                className={styles.Button}
                                parent
                                disabled={loadingCancel}
                            />
                            <ButtonTextIcon
                                type={'custom'}
                                customText={t('no')}
                                customIcon={<Close />}
                                onClick={handleClose}
                                className={styles.Button}
                                parent
                            />
                        </div>
                    </DialogBody>
                </Dialog>
            )}
        </>
    )
}

export default CancelSlotRequest
